import { useEndpoint } from '@apparatix/composables';
import { useContactStore } from '@apparatix/stores/useContactStore';
import {
  ApiResponseBase,
  GetContactDetailResponse,
  type Address,
  type ContactPayload,
  type CreateContactResponse,
  type CreatedContact,
  type DuplicateContactResponse,
  type EmailAddress,
  type InitialStateResponse,
} from '@apparatix/types/Contacts';
import {
  AssociationContact, ContactSearchResponse, ContactSearchResult, PotentialConflictResponse, ContactLabel,
} from '@apparatix/types/Leads';
import { useToastNotifications, useUtilities } from '@apparatix/composables';

export const useContacts = () => {
  const store = useContactStore();
  const { showError, showWarning } = useToastNotifications();
  const { arrayHasValues } = useUtilities();

  const filterEndpoint = useEndpoint<InitialStateResponse>({
    url: '/crm/contacts/filter',
    type: 'POST',
    successField: 'data',
    onSuccess: response => {
      store.contacts = response.data.contacts;
      store.isLoading = false;
    },
    toast: { showOnSuccess: false },
  });

  const createEndpoint = useEndpoint<CreateContactResponse>({
    type: 'POST',
    url: '/crm/contacts',
    successField: 'id',
    toast: { showOnSuccess: false },
  });


  const updateEndpoint = useEndpoint<any>({
    type: 'PUT',
    url: '/crm/contacts',
    successField: 'data',
    onSuccess: response => {
      store.contactDetails = response.data.contacts[0];

      if (arrayHasValues(response.data?.errors)) {
        response.data.errors.forEach(error => {
          showError(error, 'Error', 8000);
        });
      }

      if (store.selectedContact && store.selectedContact.id === store.contactDetails.id) {
        store.selectedContact.fullName = store.contactDetails.fullName;
        store.selectedContact.company = store.contactDetails.companyName;
        store.selectedContact.address = store.contactDetails.addressFormatted;
        store.selectedContact.emails = store.contactDetails.emailsFormatted;
        store.selectedContact.phoneNumbers = store.contactDetails.phoneNumbersFormatted;
      }
    },
    toast: { showOnSuccess: false },
  });

  const searchEndpoint = useEndpoint<ContactSearchResponse>({
    type: 'GET',
    url: '',
    successField: 'data',
    toast: { showOnSuccess: false },
  });

  const deleteEndpoint = useEndpoint<PotentialConflictResponse>({
    type: 'DELETE',
    url: '',
    successField: 'success',
    onError: (response: PotentialConflictResponse) => {
      if (response.conflict) {
        showWarning(response.conflict, 'Action Not Allowed', 180 * 1000);
        return;
      }
      showError(response.error);
    },
    toast: { showOnSuccess: false },
  });

  const duplicateEndpoint = useEndpoint<DuplicateContactResponse>({
    type: 'POST',
    successField: 'data',
    toast: {
      showOnSuccess: false,
    },
  });

  const detailEndpoint = useEndpoint<ApiResponseBase<GetContactDetailResponse>>({
    type: 'GET',
    successField: 'data',
    toast: {
      showOnSuccess: false,
    },
  });

  const deleteContact = async () => {
    const url = `/crm/contacts/${store.selectedContact.id}`;
    deleteEndpoint.setEndpointUrl(url);
    const results = await deleteEndpoint.getResults();

    if (results?.success) {
      const index = store.contacts.findIndex(contact => contact.id === store.selectedContact.id);

      if (index !== -1) {
        store.contacts.splice(index, 1);
      }
    }
  };

  const updateContact = async (payload: any) => {
    await updateEndpoint.makeRequest(payload);
  };

  const createContact = async (payload: ContactPayload) => {
    const result = await createEndpoint.getResults({ data: { contact: payload }});
    return result;
  };

  const duplicateContact = async (contactId: number): Promise<CreatedContact> => {
    const url = `/crm/contacts/duplicate/${contactId}`;
    duplicateEndpoint.setEndpointUrl(url);
    const response = await duplicateEndpoint.getResults();
    if (response?.data?.contact) {
      return response.data.contact;
    }

    return undefined;
  };

  const searchContacts = async (query: string, resultsCallback: (results: ContactSearchResult[]) => void) => {
    const url = `/crm/contacts/search?name=${query}`;
    searchEndpoint.setEndpointUrl(url);
    const results = await searchEndpoint.getResults();
    if (!results?.data?.contacts?.length) {
      resultsCallback([]);
      return;
    }

    // eslint-disable-next-line camelcase
    results.data.contacts.forEach(contact => contact.full_name = `${contact.first_name} ${contact.last_name}`);

    resultsCallback(results.data.contacts);
  };

  const mapSearchResultToAssociation = (result: ContactSearchResult): AssociationContact => {
    if (!result) {
      return undefined;
    }

    const contact: AssociationContact = {
      id: result.id,
      c2Id: result.id,
      firstName: result.first_name,
      lastName: result.last_name,
      fullName: result.full_name ?? `${result.first_name} ${result.last_name}`,
      contactLabels: [],
      companyName: result.company_name,
      ownerName: result.ownerName,
    };

    if (result.addresses?.length) {
      contact.addresses = result.addresses;
    }

    if (result.addressEmails?.length) {
      contact.addressEmails = result.addressEmails;
    }

    if (result.addressPhoneNumbers?.length) {
      contact.addressPhoneNumbers = result.addressPhoneNumbers;
    }

    return contact;
  };

  // ignoreLoadStatus will force the data to be re-fetched. Otherwise it will only fetch data if there are no contacts in the store
  const loadContactsHome = (ignoreLoadStatus: boolean = false) => {
    store.isLoading = true;
    if (store.contacts?.length === 0 || ignoreLoadStatus) {
      filterEndpoint.makeRequest({ filter: {}});
    } else {
      store.isLoading = false;
    }
  };

  const getContactLabelChipSeverity = (label: ContactLabel) => {
    switch (label.contactLabelDisplayName) {
      case 'Primary':
        return 'primary';
      case 'Signer':
        return 'warn';
      case 'Bill To':
        return 'success';
      default:
        return 'info';
    }
  };

  const emailsToString = (emails: EmailAddress[]) => {
    if (!emails?.length) return undefined;
    return emails.map(address => address.email).join(',');
  };

  const addressToString = (address: Address) => {
    if (!address) return undefined;
    return `${address.address_line1 ?? ''} ${address.city ?? ''}, ${address.region ?? ''} ${address.postal_code ?? ''}`;
  };

  const getContactDetails = async (contactId: number) => {
    detailEndpoint.setEndpointUrl(`/crm/contacts/${contactId}`);
    const response = await detailEndpoint.getResults();
    return response?.data?.contact;
  };

  return {
    addressToString,
    createContact,
    deleteContact,
    duplicateContact,
    emailsToString,
    getContactDetails,
    getContactLabelChipSeverity,
    loadContactsHome,
    mapSearchResultToAssociation,
    searchContacts,
    updateContact,
  };
};
